/*
    This way we can have a single source of truth for the keys and the values.
    From my experience, it's better to have a single source of truth for the keys, so if we need to change the key, we can do it in a single place.
*/

export class CookieStorageManager<T extends Record<string, string>> {
  private readonly _namespace: string;
  private readonly _keys: T;
  private readonly _domain: string;

  constructor(namespace: string, keys: T, domain: string) {
    this._namespace = namespace;
    this._keys = keys;
    this._domain = domain;
  }

  public getFullKey(name: keyof T): string {
    return `${this._namespace}:${this._keys[name]}`;
  }

  private _getCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookieStr of cookies) {
      const cookie = cookieStr.trim();
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
    return null;
  }

  private _setCookie(name: string, value: string, days: number = 1): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;domain=${this._domain}`;
  }

  private _removeCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${this._domain}`;
  }

  public flag(name: keyof T, defaultValue: boolean = true): boolean {
    return defaultValue
      ? this._getCookie(this.getFullKey(name)) !== 'false'
      : this._getCookie(this.getFullKey(name)) === 'true';
  }

  public has(name: keyof T): boolean {
    return this._getCookie(this.getFullKey(name)) !== null;
  }

  public get(name: keyof T): string | null {
    return this._getCookie(this.getFullKey(name));
  }

  public set(name: keyof T, value: string): void {
    this._setCookie(this.getFullKey(name), value);
  }

  public remove(name: keyof T): void {
    this._removeCookie(this.getFullKey(name));
  }
}
